import _ from "lodash";
import getModalityLUT from "./getModalityLUT.js";
import getVOILUT from "./getVOILut.js";
import calculateSUV from "component/util/calculateSUV";

/**
 * Creates a LUT used while rendering to convert stored pixel values to
 * display pixels
 *
 * @param {Image} image A  Image Object
 * @param {Number} windowWidth The Window Width
 * @param {Number} windowCenter The Window Center
 * @param {Boolean} invert A boolean describing whether or not the image has been inverted
 * @param {Array} [modalityLUT] A modality Lookup Table
 * @param {Array} [voiLUT] A Volume of Interest Lookup Table
 *
 * @returns {Uint8ClampedArray} A lookup table to apply to the image
 * @memberof Internal
 */
export default function (
  image,
  windowWidth,
  windowCenter,
  invert,
  modalityLUT,
  voiLUT
) {
  /** @TODO : Modality에 따른 LUT 생성 로직에 대한 추상화 필요 */
  const modality = _.get(image, "modality", "");
  // Modality의 경우 지정한 방법으로 LUT 생성
  if (modality.toLowerCase() === "pt") {
    // PET 영상인 경우, SUV값 기반 LUT 생성
    //return generateSUVBasedLUT(
    return generateNormalLUT(
      image,
      windowWidth,
      windowCenter,
      invert,
      modalityLUT,
      voiLUT
    );
  }
  // 예외 영상을 제외한 나머지 Modality는 NEMA CT 표준에 정의된 방법으로 LUT 생성
  return generateNormalLUT(
    image,
    windowWidth,
    windowCenter,
    invert,
    modalityLUT,
    voiLUT
  );
}
const generateSUVBasedLUT = (
  image,
  windowWidth,
  windowCenter,
  invert,
  modalityLUT,
  voiLUT
) => {
  const maxPixelValue = Math.max(image.minMax[0], image.minMax[1]);
  const minPixelValue = Math.min(image.minMax[0], image.minMax[1]);
  const offset = Math.min(minPixelValue, 0);

  if (image.cachedLut === undefined) {
    const length = maxPixelValue - offset + 1;

    image.cachedLut = {};
    image.cachedLut.lutArray = new Uint8ClampedArray(length);
  }

  const lut = image.cachedLut.lutArray;
  const mlutfn = getModalityLUT(
    image.rescaleSlope,
    image.rescaleIntercept,
    modalityLUT
  );
  const vlutfn = getVOILUT(windowWidth, windowCenter, voiLUT);
  const maxInVOI = windowCenter + windowWidth / 2;
  const minInVOI = windowCenter - windowWidth / 2;
  const minMax = image.minMax;
  const minSUV = calculateSUV(image, minInVOI);
  const maxSUV = calculateSUV(image, maxInVOI);
  // const minSUV = calculateSUV(image, minMax[0]);
  // const maxSUV = calculateSUV(image, minMax[1]);

  for (
    let storedValue = minPixelValue;
    storedValue <= maxPixelValue;
    storedValue++
  ) {
    // VOI 범위 밖 픽셀 값 필터링
    const unitValueInVOI = vlutfn(mlutfn(storedValue));
    if (unitValueInVOI >= 255) {
      lut[storedValue + -offset] = 255;
      continue;
    } else if (unitValueInVOI <= 0) {
      lut[storedValue + -offset] = 0;
      continue;
    }

    let suv = calculateSUV(image, storedValue);
    let normalizedSUV = ((suv - minSUV) / maxSUV) * 255;
    lut[storedValue + -offset] = normalizedSUV;
  }

  return lut;
};
const generateNormalLUT = (
  image,
  windowWidth,
  windowCenter,
  invert,
  modalityLUT,
  voiLUT
) => {
  const maxPixelValue = Math.max(image.minMax[0], image.minMax[1]);
  const minPixelValue = Math.min(image.minMax[0], image.minMax[1]);
  const offset = Math.min(minPixelValue, 0);

  if (image.cachedLut === undefined) {
    const length = maxPixelValue - offset + 1;

    image.cachedLut = {};
    image.cachedLut.lutArray = new Uint8ClampedArray(length);
  }

  const lut = image.cachedLut.lutArray;
  const mlutfn = getModalityLUT(
    image.rescaleSlope,
    image.rescaleIntercept,
    modalityLUT
  );
  const vlutfn = getVOILUT(windowWidth, windowCenter, voiLUT);

  if (invert === true) {
    for (
      let storedValue = minPixelValue;
      storedValue <= maxPixelValue;
      storedValue++
    ) {
      lut[storedValue + -offset] = 255 - vlutfn(mlutfn(storedValue));
      //lut[storedValue + -offset] = 100;
    }
  } else {
    for (
      let storedValue = minPixelValue;
      storedValue <= maxPixelValue;
      storedValue++
    ) {
      lut[storedValue + -offset] = vlutfn(mlutfn(storedValue));
      //lut[storedValue + -offset] = 100;
    }
  }
  return lut;
};
